import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { MutationSchema } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { MOVE_PROJECT_CHILD } from "shared/types/AppEvent"
import { Floor } from "shared/types/Floor"
import { FloorTypename } from "shared/types/Project"
import { UPDATE_FLOOR } from "shared/types/ProjectMutations"
import {
  FloorSchema,
  UpdateFloorInputSchema
} from "shared/types/__generated__/validation"

import {
  UpdateFloorDocument,
  UpdateFloorMutation,
  UpdateFloorMutationVariables
} from "entities/floors/graphql/mutations.generated"
import { didMove } from "entities/project-children/lib/didMove"
import { updateProjectChildrenSorting } from "entities/project-children/lib/updateProjectChildrenSorting"
import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import z from "zod"

import { prepareFloorUpdate } from "../model/prepareFloorUpdate"
import { FloorUpdate } from "../types/Floor"

const UpdateFloorVariablesSchema = z.object({
  id: z.string(),
  input: UpdateFloorInputSchema()
})

export function useUpdateFloor() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Floor.UPDATE_FLOOR.Start"),
      success: t("Request.Floor.UPDATE_FLOOR.Success"),
      failure: t("Request.Floor.UPDATE_FLOOR.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()

  const [apolloMutate, rest] = useApolloMutation<
    UpdateFloorMutation,
    UpdateFloorMutationVariables
  >(UpdateFloorDocument)

  const updateFloor = useEvent(
    async ({
      projectID,
      parentID,
      displayPosition,
      floor
    }: {
      projectID: string
      parentID?: string | null
      displayPosition?: number
      floor: FloorUpdate
    }) => {
      const { id } = floor
      const { changedValues, input, children, optimisticResponse, moveData } =
        prepareFloorUpdate({
          floor,
          displayPosition,
          cache: apolloCache,
          projectID,
          parentID,
          childType: "Floor"
        })

      if (Object.keys(changedValues).length === 0 && !didMove(moveData))
        return undefined

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: UPDATE_FLOOR
      })

      if (moveData.nextParentID && moveData.nextParentType) {
        emitAppEvent(MOVE_PROJECT_CHILD, {
          id: floor.id,
          parentID: moveData.nextParentID,
          parentType: moveData.nextParentType,
          position: moveData.nextPosition,
          type: FloorTypename,
          projectID
        })
      }

      return callMutation(apolloMutate, {
        variables: {
          id,
          input: didMove(moveData)
            ? {
                ...input,
                parent: {
                  id: moveData.nextParentID,
                  type: moveData.nextParentType
                }
              }
            : input
        },
        transform: data => data.floor as Floor,
        schemas: {
          input: UpdateFloorVariablesSchema,
          response: MutationSchema.extend({ floor: FloorSchema() })
        },
        apolloOptions: {
          optimisticResponse,
          update: (cache, result) => {
            if (didMove(moveData)) {
              const floorResult: Floor | null = result.data?.floor
                ? { ...result.data.floor, children }
                : null
              if (
                floorResult &&
                moveData.nextParentID &&
                moveData.nextParentType &&
                moveData.prevParentID &&
                moveData.prevParentType
              ) {
                updateProjectChildrenSorting({
                  movedChild: floorResult,
                  prevParentID: moveData.prevParentID,
                  prevParentType: moveData.prevParentType,
                  nextParentID: moveData.nextParentID,
                  nextParentType: moveData.nextParentType,
                  cache
                })
              }
            }
          },
          onCompleted: () => {
            fetchProject({
              variables: { id: projectID },
              fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
            })
            evictProjectHistory(projectID, apolloCache)
            completeProjectRequest()
            raiseSuccessToast()
          },
          onError: () => {
            fetchProject({
              variables: { id: projectID },
              fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
            })
            if (moveData) {
              evictProjectPrice(projectID, apolloCache, true)
            }
            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [updateFloor, rest] as [typeof updateFloor, typeof rest]
}
